import domReady from '@roots/sage/client/dom-ready'
import Emitter from 'tiny-emitter'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'
import { Draggable } from 'gsap/Draggable'
import { InertiaPlugin } from 'gsap/InertiaPlugin'
import { throttle } from 'throttle-debounce'
import { listen } from 'quicklink'
import 'lite-youtube-embed'
import { getGPUTier } from 'detect-gpu'

// Utils
import breakpoints from '../breakpoints.json'
import Header from './partials/Header'
import Intro from './partials/Intro'
import ScrollManager from './managers/ScrollManager'
import RoutesManager from './managers/RoutesManager'
// import CookiesManager from './managers/CookiesManager'
import WebGLManager from './webgl/WebGLManager'
import Cursor from './components/Cursor'

export default class App {
  /**
   * Loads the page, then calls `this.start()` when DOM is ready.
   *
   * @constructor
   */
  constructor() {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual'

    gsap.registerPlugin(ScrollTrigger, Draggable, InertiaPlugin, SplitText)
    this.bindMethods()

    App.pointer = {
      x: 0, y: 0, normalized: {
        x: 0, y: 0
      }
    }
    App.isTransitioning = true
    App.isReady = false
    App.windowWidth = window.innerWidth
    App.windowHeight = window.innerHeight

    App.scrollY = 0
    App.time = 0
    App.domLocked = document.body.querySelector('.js-app-locked')
    App.domWrapper = document.body.querySelector('.app--wrapper')
    App.domScroll = document.body.querySelector('.app--scroller')
    App.intro = document.body.querySelector('.js-app-intro')

    this.setBreakpoints()
    this.setVH()
    this.checkDevice()

    // App.CM = new CookiesManager()
    App.SM = new ScrollManager()
    App.RM = new RoutesManager(this)
    App.scroll(false)

    this.cursor = new Cursor(document.body)
    App.cursor = this.cursor

    this.emitter = new Emitter()
    App.Emitter = this.emitter

    document.fonts.ready.then((font) => {
      App.yieldToMain()
        // UNCOMMENT TO ENABLE WEBGL !
        // .then(this.createWebGLManager.bind(this))
        .then(this.configChecker.bind(this))
        .then(() => {
          if (window.DEV && window.DEV.gui) return window.DEV.gui.createFPSGraph()

          return Promise.resolve()
        })
        .then(() => {
          // Starts the Menu
          this.header = new Header()
          App.header = this.header
        })
        .then(App.RM.init)
        .then(this.start.bind(this))
        .then(App.yieldToMain)
    })
  }

  /**
   * Here you can bind your methods to **this** instance.
   *
   * @private Called by `constructor`.
   * @returns {void}
   */
  bindMethods() {
    this.resize = this.resize.bind(this)
    this.pointermove = this.pointermove.bind(this)
    this.render = this.render.bind(this)
    this.ready = this.ready.bind(this)

    this.resize = throttle(200, this.resize)
  }

  /**
   * Sets the breakpoints as numbers in a static object name `App.breakpoints`.
   *
   * @returns {void}
   */
  setBreakpoints() {
    App.breakpoints = {}
    App.device = {}

    for (const key in breakpoints) {
      App.device[key] = false
      App.breakpoints[key] = breakpoints[key]
    }
  }

  /**
   * Starts everything that is app-wide.
   *
   * Then starts the `appear()` animation of the current page.
   *
   * @returns {Promise} - A Promise resolved when the work is done.
   */
  start() {
    // Starts listening to events
    this.events()
    App.listenQuicklink()

    this.intro = new Intro({ skip: true })

    ScrollTrigger.refresh()

    // Starts animation loop
    gsap.ticker.add(this.render)
    App.currentRenderer.mask()

    gsap.set(App.domWrapper, { alpha: 1 })

    const promises = [App.currentRenderer.load(), this.intro.load()]

    if (App.GLM) {
      promises.push(App.GLM.loadFirstPage())
      promises.push(this.loadGLAssets())
    }

    Promise.all(promises)
      .then(() => {
        App.currentRenderer.onFirstEnter()
      })
      .then(this.ready)
      .then(this.intro.hide)
  }

  /**
   * Called when the app is ready to start.
   * Unlocks the scroll to let the user navigate freely.
   *
   * @returns {void}
   */
  ready() {
    const tl = gsap.timeline({
      onComplete: () => {
        App.currentRenderer.onFirstEnterCompleted()
        ScrollTrigger.refresh()
        App.scroll(true)
        App.locked(false)
        App.isTransitioning = false
        App.isReady = true
        App.intro.remove()
      }
    })

    tl.to(App.intro, { duration: 1, opacity: 0, ease: 'quad.out' })
    tl.addLabel('step1', '-=0.5')
    tl.add(App.currentRenderer.appear(), '-=0.5')
    tl.to(App.header.el, { duration: 1, opacity: 1, ease: 'quad.out' }, 'step1+=1')
  }

  static scroll(bool) {
    App.SM.toggle(bool)
  }

  static locked(bool) {
    App.domLocked.classList.toggle('!block', bool)
  }

  async createWebGLManager() {
    if (App.GLM) return Promise.resolve()

    const webGLManager = await import('./webgl/WebGLManager')

    App.GLM = new webGLManager.default({
      rendererOptions: {
        powerPreference: 'high-performance', alpha: true, antialias: true
      }
    })

    return App.yieldToMain()
  }

  static checkWebGLForPage(page) {
    if (App.GLM) App.GLM.checkWebGLForPage(page)
  }

  static listenQuicklink(page) {
    // Starts quicklink prefetching
    // @todo: test reset function on page change (returned by listen())
    listen({
      delay: 500, threshold: 0.25, ignores: [(uri, elem) => elem.hasAttribute('data-noprefetch')]
    })
  }

  /**
   * Checks the device by comparing the window width to our breakpoints.
   * Values are saved in the static object `App.device`.
   *
   * @returns {void}
   */
  checkDevice() {
    for (const key in App.breakpoints) App.device[key] = App.windowWidth >= App.breakpoints[key]
  }

  /**
   * Checks if the current device's window width is under a given breakpoint.
   *
   * @param {String} name - The breakpoint name to check for.
   *
   * @static
   * @returns {Boolean} `true` is the window width is under the given breakpoint.
   */
  static isDevice(name) {
    if (!Object.prototype.hasOwnProperty.call(App.device, name)) return false

    return App.device[name]
  }

  /**
   * Checks if the current device's has touch capacities.
   *
   * @static
   * @returns {Boolean} `true` is this is a touch device.
   */
  static isTouch() {
    return window.matchMedia('(pointer: coarse)').matches
  }

  static getColSize() {
    const count = getComputedStyle(document.documentElement).getPropertyValue('--column-count')

    return App.windowWidth / count
  }

  /**
   * Sets a CSS variable equal to 1% of the window height.
   * Useful on mobile devices where url and nav bars are not counted as a CSS `vh`.
   *
   * @returns {void}
   */
  setVH() {
    const vh = App.windowHeight * 0.01

    document.documentElement.style.setProperty('--vh', vh + 'px')
  }

  /**
   * Resize event. Throttled every 200ms.
   *
   * Automatically calls `.resize()` method of needed components.
   *
   * @returns {void}
   */
  resize() {
    App.windowWidth = window.innerWidth
    App.windowHeight = window.innerHeight

    this.setVH()
    this.checkDevice()

    App.currentRenderer.resize()
    this.header.resize()
    if (App.GLM) App.GLM.resize()
  }

  /**
   * Pointer move event.
   *
   * Automatically calls `.pointermove()` method of needed components.
   *
   * @param {PointerEvent} e - The pointer event.
   *
   * @returns {void}
   */
  pointermove(e) {
    App.pointer.x = e.clientX
    App.pointer.y = e.clientY
    App.pointer.normalized.x = e.clientX / App.windowWidth
    App.pointer.normalized.y = e.clientY / App.windowHeight

    this.cursor.pointermove(e)

    if (App.isTransitioning) return

    App.currentRenderer.pointermove(e)
    if (App.GLM) App.GLM.pointermove(e)
  }

  touchmove(e) {
    this.currentRenderer.touchmove(e)
  }

  /**
   * RAF, called every frame.
   *
   * Automatically calls `.render()` method of needed components.
   *
   * @param {Number} time - The elapsed time since the first frame.
   * @param {Number} deltaTime - The delta between this frame and the last.
   *
   * @returns {void}
   */
  render(time, deltaTime) {
    if (window.DEV && window.DEV.gui && window.DEV.gui.fpsGraph) window.DEV.gui.fpsGraph.begin()

    App.SM.render(time)

    if (App.GLM) {
      App.GLM.render({
        time, deltaTime
      })
    }

    if (!App.isTransitioning) App.currentRenderer.render(time, deltaTime)

    if (window.DEV && window.DEV.gui && window.DEV.gui.fpsGraph) window.DEV.gui.fpsGraph.end()
  }

  /**
   * Starts all the app events.
   *
   * - `resize` / `orientationchange`
   * - `pointermove`
   *
   * @returns {void}
   */
  events() {
    window.addEventListener('resize', this.resize)
    window.addEventListener('pointermove', this.pointermove)
    window.addEventListener('touchmove', this.touchmove)
    window.addEventListener('orientationchange', this.resize)

    App.SM.events()
    App.RM.events()
  }

  /**
   * Yield to main thread.
   * Use this static method to break long tasks in smaller ones.
   *
   * @source https://web.dev/optimize-long-tasks/#use-asyncawait-to-create-yield-points
   * @static
   * @returns {Promise} A Promise resolved immediately
   */
  static yieldToMain() {
    return new Promise((resolve) => {
      setTimeout(resolve, 0)
    })
  }

  /**
   * Loads current page's gl assets.
   * WEBGL CODE IS COMMENTED BY DEFAULT.
   * Read the comments carefully.
   *
   * @returns {Promise} A Promise resolved when all needed items are loaded.
   */
  loadGLAssets() {
    return new Promise((resolve) => {
      App.currentRenderer.checkWebGL()
      // If the WebGLManager has been created, and the first page has a PageGL instanced attached to it,
      // Load its assets, then tell the WebGLManager that it's ready to go.
      // NOTE: this is the place where you can add a loading animation and end it as soon as the `prepare()` method has returned.
      if (App.webglManager && App.currentRenderer.pageGL) {
        App.currentRenderer.pageGL.prepare()
          .then(() => {
            // eslint-disable-next-line no-undef
            WebGLManager.setCurrentPage(App.currentRenderer.pageGL)
            // eslint-disable-next-line no-undef
            WebGLManager.ready()

            resolve()
          })
      } else if (App.GLM) {
        WebGLManager.ready()

        resolve()
      }

      // IF THE WEBGL CODE IN THIS METHOD IS UNCOMMENTED, YOU MUST DELETE THIS LINE !
      // resolve()
    })
  }

  /**
   *
   * Get the current tier performance of the device.
   *
   *
   * @returns {number} The performance indicator for the user.
   */
  configChecker() {
    const array = ['intel iris plus', 'intel corporation iris graphics 6100', 'amd radeon pro 570 opengl engine'];

    return new Promise((resolve) => {
      getGPUTier().then((gpuTier) => {
        let bodyClass = 'app--full';

        App.GPU = {
          light: array.indexOf(gpuTier.gpu) > -1 || (gpuTier.fps && gpuTier.tier < 2),
          medium: array.indexOf(gpuTier.gpu) > -1 || (gpuTier.fps && gpuTier.tier > 1 && gpuTier.tier < 3),
          tierPerf: gpuTier.tier
        }

        // App.GPU.light = true

        App.GPU.hight = !App.GPU.light && !App.GPU.medium
        if (App.GPU.light) bodyClass = 'app--light'
        if (App.GPU.medium) bodyClass = 'app--medium'

        document.body.classList.add(bodyClass)
        document.body.dataset.tier = gpuTier.tier

        resolve()
      })
    })

  }
}

/**
 * Application entrypoint
 */
domReady(async () => {
  if (wp_vars.isDev) await import('./dev/dev.js')
  new App()
})

/**
 * @see {@link https://webpack.js.org/api/hot-module-replacement/}
 */
import.meta.webpackHot?.accept(console.error)
